import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { CONTACT_EMAIL, CONTACT_EMAIL_MAILTO, INSTAGRAM_URL } from "@/lib/contact-links";
import { Modal, ModalActions } from "@/components/Modal";
import { useAuth } from "@/hooks/useAuth";
import { useWedding } from "@/hooks/useWedding";
import { reportError } from "@/lib/report-error";
import { submitFeedback, uploadFeedbackScreenshot } from "./operations";
import { ThankYouAnimation } from "./ThankYouAnimation";

interface FeedbackModalProps {
  screenshot: Blob | null;
  onClose: () => void;
}

const TEXT_LIMIT = 2000;
const THANK_YOU_MS = 2200;

export function FeedbackModal({ screenshot, onClose }: FeedbackModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { wedding } = useWedding();
  const location = useLocation();
  const [text, setText] = useState("");
  const [includeScreenshot, setIncludeScreenshot] = useState(screenshot != null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();

  const previewUrl = usePreviewUrl(includeScreenshot ? screenshot : null);

  useEffect(() => {
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!user || busy || done) return;
    const trimmed = text.trim();
    if (trimmed.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      let screenshotPath: string | null = null;
      if (includeScreenshot && screenshot) {
        try {
          screenshotPath = await uploadFeedbackScreenshot(user.id, screenshot);
        } catch (err) {
          reportError("feedback.uploadScreenshot", err);
          screenshotPath = null;
        }
      }
      await submitFeedback({
        uid: user.id,
        weddingId: wedding?.id ?? null,
        route: location.pathname.slice(0, 500),
        userAgent: navigator.userAgent.slice(0, 500),
        text: trimmed,
        screenshotPath,
      });
      setDone(true);
      closeTimer.current = setTimeout(onClose, THANK_YOU_MS);
    } catch (err) {
      reportError("feedback.submit", err);
      setError(t("feedback.error"));
      setBusy(false);
    }
  }

  if (done) {
    return (
      <Modal title={t("feedback.modalTitle")} onClose={onClose} width={480} closeOnBackdrop={false}>
        <ThankYouAnimation />
      </Modal>
    );
  }

  const remaining = TEXT_LIMIT - text.length;
  const canSubmit = !busy && text.trim().length > 0;

  return (
    <Modal
      title={t("feedback.modalTitle")}
      hint={t("feedback.hint")}
      onClose={onClose}
      width={520}
      footer={
        <ModalActions>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t("feedback.cancel")}
          </button>
          <button
            type="submit"
            form="feedback-form"
            className="btn btn-primary"
            disabled={!canSubmit}
          >
            {busy ? t("feedback.sending") : t("feedback.submit")}
          </button>
        </ModalActions>
      }
    >
      <form
        id="feedback-form"
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: 14, margin: 0 }}
      >
        <label
          htmlFor="feedback-text"
          className="t-eyebrow"
          style={{ display: "block", marginBottom: -6 }}
        >
          {t("feedback.label")}
        </label>
        <textarea
          id="feedback-text"
          autoFocus
          className="input"
          rows={5}
          maxLength={TEXT_LIMIT}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("feedback.placeholder")}
          disabled={busy}
          style={{ resize: "vertical", minHeight: 96 }}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: 11,
            color: "var(--ink-3)",
          }}
        >
          <span>{t("feedback.charsRemaining", { count: remaining })}</span>
        </div>

        <aside
          aria-label={t("feedback.altChannelsAria")}
          style={{
            padding: "12px 14px",
            background: "var(--bg-2)",
            border: "1px solid var(--line)",
            borderRadius: "var(--r-md)",
            fontSize: 13,
            lineHeight: 1.45,
            color: "var(--ink-2)",
          }}
        >
          <p style={{ margin: 0 }}>
            <Trans
              i18nKey="feedback.altChannels"
              values={{ email: CONTACT_EMAIL }}
              components={{
                emailLink: (
                  <a
                    href={CONTACT_EMAIL_MAILTO}
                    style={{
                      color: "var(--accent)",
                      textDecoration: "underline",
                      textUnderlineOffset: "2px",
                    }}
                  />
                ),
                igLink: (
                  <a
                    href={INSTAGRAM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "var(--accent)",
                      textDecoration: "underline",
                      textUnderlineOffset: "2px",
                    }}
                  />
                ),
              }}
            />
          </p>
        </aside>

        {screenshot && (
          <div
            style={{
              border: "1px solid var(--line)",
              borderRadius: "var(--r-md)",
              padding: 10,
              display: "flex",
              gap: 12,
              alignItems: "center",
              background: "var(--bg-2)",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={includeScreenshot}
                onChange={(e) => setIncludeScreenshot(e.target.checked)}
                disabled={busy}
              />
              {t("feedback.includeScreenshot")}
            </label>
            {includeScreenshot && previewUrl && (
              <img
                src={previewUrl}
                alt={t("feedback.screenshotAlt")}
                style={{
                  marginLeft: "auto",
                  maxHeight: 72,
                  maxWidth: 120,
                  borderRadius: "var(--r-sm, 6px)",
                  border: "1px solid var(--line)",
                  objectFit: "cover",
                  transition: "opacity var(--t-fast)",
                }}
              />
            )}
          </div>
        )}

        {error && (
          <div role="alert" style={{ fontSize: 13, color: "var(--danger, #b00020)" }}>
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

function usePreviewUrl(blob: Blob | null) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);
  return url;
}
